/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { FloatNumberInput, MoneyInput } from '@/components';
import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  Dialog,
  DialogTitle,
  DialogPortal,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useSettings } from '@/domain/dienstplan/features/SettingsMindestbelegung/hooks/useSettings';
import { useTagebuchEinstellungen } from '@/domain/dienstplan/shared/hooks/useTagebuchEinstellungen/useTagebuchEinstellungen';
import { Arbeitsbereich } from '@/domain/dienstplan/shared/types/Arbeitsbereich';
import { AbsatzVorgabe } from '@/domain/dienstplan/shared/types/tagebuchEinstellungen';
import { Weekday } from '@/domain/dienstplan/shared/types/Weekday';
import { getLocaleWeekdayString } from '@/domain/dienstplan/shared/utils/getLocaleWeekdayString';
import { mapWochentagForAPI } from '@/domain/dienstplan/shared/utils/mapWochentagForAPI';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { Dienstplan_AbsatzVorgabe } from '@/gql/graphql';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type SettingsMindestbelegungProps = {
  wochentag: Weekday;
  arbeitsbereich: Arbeitsbereich;
};

export function SettingsMindestbelegung({
  wochentag,
  arbeitsbereich,
}: SettingsMindestbelegungProps) {
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const [updateSettings] = useSettings();
  const { manager } = useProfile();
  const [showDialog, setShowDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { umsatzplanungAnzeige, tagebuchEinstellungen } =
    useTagebuchEinstellungen(standortId);
  const einstellungen = arbeitsbereich.mindestbelegungen.find(
    (w) => w.tag === wochentag,
  ).einstellungen;

  const formSchema = z.object({
    maxMitarbeiteranzahl: z
      .string()
      .regex(/^\d+$/, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors',
        ),
      })
      .transform((val) => Number(val))
      .refine((val) => val > 0, {
        message: t(
          'domain.dienstplan.mindestbelegung.einstellungen.validationErrors',
        ),
      }),
    unbesetzteSchichten: z.enum(['yes', 'no']),
    bemerkung: z.string(),
    umsatzproduktivitaet: z.string(),
    absatzproduktivitaet: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      maxMitarbeiteranzahl: einstellungen.maxAnzahlMitarbeiter,
      unbesetzteSchichten: einstellungen.unbesetzteSchichten ? 'yes' : 'no',
      bemerkung: einstellungen.bemerkung,
      umsatzproduktivitaet: einstellungen.zielumsatzproduktivitaet.toString(),
      absatzproduktivitaet:
        tagebuchEinstellungen.absatzProduktivitaet.absatzVorgabe ===
        AbsatzVorgabe.Stueck
          ? einstellungen.zielabsatzproduktivitaet.stueckzahl.toString()
          : einstellungen.zielabsatzproduktivitaet.mpt.toString(),
    },
    resolver: zodResolver(formSchema),
  });

  async function handleSubmit(values: z.infer<typeof formSchema>) {
    const result = await updateSettings({
      input: {
        bemerkung: values.bemerkung,
        idArbeitsbereich: arbeitsbereich.id,
        managerId: manager.id,
        maxMitarbeiterAnzahl: +values.maxMitarbeiteranzahl,
        tag: mapWochentagForAPI(wochentag),
        unbesetzteSchichtenErlauben: values.unbesetzteSchichten === 'yes',
        zielabsatzproduktivitaetAbsatzvorgabe:
          tagebuchEinstellungen.absatzProduktivitaet.absatzVorgabe ===
          AbsatzVorgabe.Stueck
            ? Dienstplan_AbsatzVorgabe.Stueck
            : Dienstplan_AbsatzVorgabe.Mpt,
        zielabsatzproduktivitaetWert: +values.absatzproduktivitaet,
        zielumsatzproduktivitaet: +values.umsatzproduktivitaet,
      },
    });

    if (
      result.data.dienstplan_update_einstellungen.__typename ===
      'dienstplan_einstellungen'
    ) {
      toast({
        title: 'Mindestbelegung',
        description: 'Einstellungen erfolgreich gespeichert',
      });
      setIsOpen(false);
    }
    form.reset(values);
  }

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (open === false && form.formState.isDirty) {
            setShowDialog(true);
            return;
          }
          setIsOpen(open);
        }}>
        <SheetTrigger asChild>
          <Button variant="icon">
            <FontAwesomeIcon icon={faGear} />
          </Button>
        </SheetTrigger>
        <SheetContent className="flex flex-col gap-6">
          <SheetHeader className="flex flex-col space-y-1">
            <h1 className="text-neutrals-N700 text-sm">
              {getLocaleWeekdayString(wochentag)}
            </h1>
            <h2 className="text-neutrals-N900 text-xl font-bold">
              {arbeitsbereich.name}
            </h2>
          </SheetHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                {umsatzplanungAnzeige !== 'ABSATZ_PRODUKTIVITAET' && (
                  <FormField
                    control={form.control}
                    name="absatzproduktivitaet"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>
                            {t(
                              'domain.dienstplan.mindestbelegung.einstellungen.umsatz',
                            )}
                          </FormLabel>
                          <FormControl>
                            <MoneyInput
                              isIconVisible={false}
                              {...field}
                              unit={t(
                                'domain.controlling.umsatzplanung.ziele.€/Std.',
                              )}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                )}
                {umsatzplanungAnzeige !== 'UMSATZ_PRODUKTIVITAET' && (
                  <FormField
                    control={form.control}
                    name="umsatzproduktivitaet"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>
                            {t(
                              'domain.dienstplan.mindestbelegung.einstellungen.absatz',
                            )}
                          </FormLabel>
                          <FormControl>
                            <FloatNumberInput
                              {...field}
                              unit={
                                tagebuchEinstellungen.absatzProduktivitaet
                                  .absatzVorgabe === AbsatzVorgabe.Stueck
                                  ? t(
                                      'domain.controlling.umsatzplanung.units.Stk./Std.',
                                    )
                                  : t(
                                      'domain.controlling.umsatzplanung.units.mpt',
                                    )
                              }
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                )}
                <FormField
                  control={form.control}
                  name="maxMitarbeiteranzahl"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>
                          {t(
                            'domain.dienstplan.mindestbelegung.einstellungen.mitarbeiterMax',
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="unbesetzteSchichten"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>
                          {t(
                            'domain.dienstplan.mindestbelegung.einstellungen.schichten',
                          )}
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            className="flex gap-4"
                            onValueChange={field.onChange}
                            defaultValue={field.value}>
                            <FormItem className="flex items-center gap-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="yes" />
                              </FormControl>
                              <FormLabel>{t('actions.yes')}</FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center gap-2 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="no" />
                              </FormControl>
                              <FormLabel>{t('actions.no')}</FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="bemerkung"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>
                          {t(
                            'domain.dienstplan.mindestbelegung.einstellungen.bemerkung',
                          )}
                        </FormLabel>
                        <FormControl>
                          <Textarea maxLength={250} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
              <div className="flex w-full justify-end">
                <Button disabled={form.formState.isSubmitting} type="submit">
                  {t(
                    'domain.dienstplan.mindestbelegung.einstellungen.speichern',
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
      {showDialog && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogPortal>
            <DialogContent className="z-[55]">
              <DialogTitle>
                {t(
                  'domain.dienstplan.mindestbelegung.einstellungen.dialog.title',
                )}
              </DialogTitle>
              <DialogDescription>
                <div className="flex flex-col gap-8 w-full">
                  <p>
                    {t(
                      'domain.dienstplan.mindestbelegung.einstellungen.dialog.despription',
                    )}
                  </p>
                  <div className="flex justify-end w-full gap-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        form.reset();
                        setShowDialog(false);
                        setIsOpen(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.verlassen',
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowDialog(false);
                      }}>
                      {t(
                        'domain.dienstplan.mindestbelegung.einstellungen.dialog.bearbeiten',
                      )}
                    </Button>
                  </div>
                </div>
              </DialogDescription>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  );
}
